import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Database, Users, LineChart, Check, X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import VisualComparisonLeft from './VisualComparisonLeft';
import VisualComparisonRight from './VisualComparisonRight';
import { Helmet } from 'react-helmet-async';

const ImprovedProductComparison: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Intersection Observer for performance
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Cleanup after animation triggers
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px' // Start loading slightly before visible
      }
    );

    const section = document.getElementById('product-comparison');
    if (section) {
      observer.observe(section);
    }

    return () => observer.disconnect();
  }, []);

  // Animation variants optimized for performance
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.6,
        ease: 'easeOut'
      } 
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        ease: 'easeOut'
      }
    }
  };

  // Structured data for SEO
  const productComparisonSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Strive Data Analysis Platform",
    "description": "AI-powered customer feedback analysis and prioritization platform",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    }
  };

  const comparisonPoints = [
    {
      title: "Data Collection",
      traditional: "Manual collection across multiple channels",
      strive: "Automated extraction from all communication channels",
      traditionalIcon: <X className="h-5 w-5 text-red-500" aria-hidden="true" />,
      striveIcon: <Check className="h-5 w-5 text-green-500" aria-hidden="true" />
    },
    {
      title: "Processing",
      traditional: "Siloed in disconnected systems",
      strive: "Centralized in one intelligent platform",
      traditionalIcon: <X className="h-5 w-5 text-red-500" aria-hidden="true" />,
      striveIcon: <Check className="h-5 w-5 text-green-500" aria-hidden="true" />
    },
    {
      title: "Analysis",
      traditional: "Manual review and categorization",
      strive: "AI-powered pattern recognition and categorization",
      traditionalIcon: <X className="h-5 w-5 text-red-500" aria-hidden="true" />,
      striveIcon: <Check className="h-5 w-5 text-green-500" aria-hidden="true" />
    },
    {
      title: "Prioritization",
      traditional: "Based on gut feeling or loudest voice",
      strive: "Data-driven with revenue impact assessment",
      traditionalIcon: <X className="h-5 w-5 text-red-500" aria-hidden="true" />,
      striveIcon: <Check className="h-5 w-5 text-green-500" aria-hidden="true" />
    },
    {
      title: "Action",
      traditional: "Slow, manual follow-up",
      strive: "Automated workflows and notifications",
      traditionalIcon: <X className="h-5 w-5 text-red-500" aria-hidden="true" />,
      striveIcon: <Check className="h-5 w-5 text-green-500" aria-hidden="true" />
    }
  ];

  const benefits = [
    {
      icon: <Zap className="h-6 w-6 text-amber-500" aria-hidden="true" />,
      title: "Faster Insights",
      description: "Upload your docs and CSVs. Strive extracts what's important - no manual tagging, no spreadsheet wrangling."
    },
    {
      icon: <Database className="h-6 w-6 text-blue-500" aria-hidden="true" />,
      title: "Real-World Data, Unified",
      description: "Merge structured data and written content into one place. Strive works across feedback, notes, and product artifacts."
    },
    {
      icon: <Users className="h-6 w-6 text-purple-500" aria-hidden="true" />,
      title: "Aligned Teams, Shared Context",
      description: "Whether you're reporting up or coordinating across, Strive gives everyone a clear view of what matters and why."
    },
    {
      icon: <LineChart className="h-6 w-6 text-green-500" aria-hidden="true" />,
      title: "Prioritize What Moves the Needle",
      description: "Strive helps you spot high-impact opportunities using real data - so you focus on what drives progress."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Product Comparison - Strive vs Traditional Approaches | Data Analysis Platform</title>
        <meta name="description" content="Compare Strive's AI-powered data analysis platform with traditional approaches. See how we automate feedback collection and provide actionable insights." />
        <meta property="og:title" content="Strive vs Traditional Data Analysis - See the Difference" />
        <meta property="og:description" content="Compare Strive's AI-powered data analysis platform with traditional approaches. See how we automate feedback collection and provide actionable insights." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(productComparisonSchema)}
        </script>
      </Helmet>

      <section 
        id="product-comparison" 
        className="py-24 bg-gradient-to-b from-white to-gray-50"
        aria-label="Product Comparison Section"
      >
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            variants={fadeIn}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 tracking-tight max-w-xl mx-auto">
              One tool to tell your complete product story
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Strive doesn't replace your stack. It connects the dots across your tools and surfaces the insights you need to lead with clarity and confidence.
            </p>
          </motion.div>

          <Tabs defaultValue="visual" className="w-full max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 mb-8" aria-label="Comparison views">
              <TabsTrigger value="visual">Visual Comparison</TabsTrigger>
              <TabsTrigger value="detailed">Detailed Breakdown</TabsTrigger>
            </TabsList>
            
            <TabsContent value="visual" className="mt-0">
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-24 relative"
                initial="hidden"
                animate={isVisible ? "visible" : "hidden"}
                variants={staggerChildren}
              >
                {/* Left Column - "Before" */}
                <motion.div variants={fadeIn} className="flex">
                  <VisualComparisonLeft />
                </motion.div>
                
                {/* Center Arrow */}
                <motion.div 
                  className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.5 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-full shadow-lg">
                    <ArrowRight className="h-8 w-8" aria-hidden="true" />
                  </div>
                </motion.div>
                
                {/* Right Column - "After" */}
                <motion.div variants={fadeIn} className="flex">
                  <VisualComparisonRight />
                </motion.div>
              </motion.div>
            </TabsContent>
            
            <TabsContent value="detailed">
              <motion.div 
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                initial="hidden"
                animate={isVisible ? "visible" : "hidden"}
                variants={fadeIn}
              >
                {/* Header - Hidden on mobile, shown on desktop */}
                <div className="hidden md:grid md:grid-cols-3 bg-gray-50 p-4 border-b border-gray-200">
                  <div className="font-semibold text-gray-700">Process Step</div>
                  <div className="font-semibold text-gray-700">Traditional Approach</div>
                  <div className="font-semibold text-blue-700">Strive Solution</div>
                </div>
                
                {comparisonPoints.map((point, index) => (
                  <motion.div 
                    key={index}
                    className={`md:grid md:grid-cols-3 p-4 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-100`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {/* Process Step - Full width on mobile */}
                    <div className="font-medium text-gray-900 mb-3 md:mb-0">{point.title}</div>
                    
                    {/* Mobile-optimized comparison layout */}
                    <div className="space-y-3 md:space-y-0 md:col-span-2 md:grid md:grid-cols-2">
                      {/* Traditional Approach */}
                      <div className="flex items-start gap-2 text-gray-600 bg-white/50 p-2 md:p-0 rounded-lg md:rounded-none">
                        <div className="mt-1">
                          {point.traditionalIcon}
                        </div>
                        <div className="flex-1">
                          <div className="text-xs uppercase text-gray-500 mb-1 md:hidden">Traditional Approach</div>
                          <span>{point.traditional}</span>
                        </div>
                      </div>
                      
                      {/* Strive Solution */}
                      <div className="flex items-start gap-2 text-gray-800 bg-blue-50/50 p-2 md:p-0 rounded-lg md:rounded-none">
                        <div className="mt-1">
                          {point.striveIcon}
                        </div>
                        <div className="flex-1">
                          <div className="text-xs uppercase text-blue-600 mb-1 md:hidden">Strive Solution</div>
                          <span className="font-medium">{point.strive}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </TabsContent>
          </Tabs>
          
          <motion.div 
            className="mt-20"
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            variants={staggerChildren}
          >
            <h3 className="text-2xl font-bold text-center mb-10">Key Benefits</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {benefits.map((benefit, index) => (
                <motion.div 
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                  variants={fadeIn}
                >
                  <div className="mb-4 p-3 rounded-full bg-gray-50 inline-block">
                    {benefit.icon}
                  </div>
                  <h4 className="text-xl font-semibold mb-2">{benefit.title}</h4>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ImprovedProductComparison; 